import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Cloud,
  Database,
  FileAudio,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw,
  Search,
  PlayCircle,
  XCircle,
  HelpCircle,
  Zap,
  Inbox,
  Activity,
  Bolt,
  PowerOff,
  Folder,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  RotateCw,
} from 'lucide-react';

const apiBase = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('saas_token') || localStorage.getItem('token') || '';

const api = async (path, opts = {}) => {
  const res = await fetch(`${apiBase}${path}`, {
    ...opts,
    headers: {
      'Authorization': `Bearer ${getToken()}`,
      'Content-Type': 'application/json',
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      detail = j.detail || j.message || JSON.stringify(j);
      if (Array.isArray(detail)) {
        detail = detail.map(d => d.msg || JSON.stringify(d)).join('; ');
      }
    } catch (_) {
      try { detail = (await res.text()) || detail; } catch (_) {}
    }
    throw new Error(detail);
  }
  return res.json();
};

// ----------------------------------------------------------------------------
// Status meta
// ----------------------------------------------------------------------------
const STATUS_META = {
  unsynced:           { label: 'Not synced',       color: 'bg-orange-100 text-orange-800 border-orange-300', icon: Inbox,           desc: 'In OneDrive, not yet in DB' },
  pending_processing: { label: 'Pending',          color: 'bg-blue-100 text-blue-800 border-blue-300',       icon: Clock,           desc: 'Synced, waiting in queue' },
  processing:         { label: 'Processing',       color: 'bg-violet-100 text-violet-800 border-violet-300', icon: PlayCircle,      desc: 'Being processed right now' },
  completed:          { label: 'Completed',        color: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2, desc: 'Successfully processed' },
  failed:             { label: 'Failed',           color: 'bg-red-100 text-red-800 border-red-300',         icon: AlertTriangle,   desc: 'Processing failed' },
  file_missing:       { label: 'File missing',     color: 'bg-rose-100 text-rose-800 border-rose-300',      icon: XCircle,         desc: 'File no longer in OneDrive' },
  quarantined:        { label: 'Quarantined',      color: 'bg-amber-100 text-amber-800 border-amber-300',   icon: HelpCircle,      desc: 'Max retries exceeded' },
  unknown:            { label: 'Unknown',          color: 'bg-slate-100 text-slate-800 border-slate-300',   icon: HelpCircle,      desc: 'No status' },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.unknown;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={m.color} data-testid={`status-badge-${status}`}>
      <Icon className="h-3 w-3 mr-1" />
      {m.label}
    </Badge>
  );
}

function fmtBytes(n) {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtRelative(iso) {
  if (!iso) return '—';
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 5) return 'just now';
  if (d < 60) return `${Math.floor(d)}s ago`;
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return new Date(iso).toLocaleDateString();
}

function fmtDuration(seconds) {
  if (seconds == null) return '—';
  if (seconds < 0) return 'expired';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
}

// ----------------------------------------------------------------------------
// Live Deltas card
// ----------------------------------------------------------------------------
function LiveDeltasCard({ status, events, onCreate, onRenew, onDelete, busy }) {
  const active = status?.active;
  return (
    <Card data-testid="live-deltas-card" className={`border-2 ${active ? 'border-emerald-300 dark:border-emerald-800' : 'border-slate-200 dark:border-slate-800'}`}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className={`relative h-14 w-14 rounded-2xl ${active ? 'bg-emerald-500' : 'bg-slate-400'} flex items-center justify-center shadow-lg`}>
              <Bolt className="h-7 w-7 text-white" />
              {active && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-yellow-300 ring-2 ring-white animate-pulse" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-semibold tracking-tight">Live Deltas (Webhook)</h2>
                <Badge variant="outline" className={active ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}>
                  {active ? 'Active' : 'Inactive'}
                </Badge>
              </div>
              {active ? (
                <p className="text-sm text-slate-500 mt-1">
                  Microsoft Graph pushes notifications instantly · {status?.notification_count ?? 0} received
                  {status?.last_notification_at && <> · last {fmtRelative(status.last_notification_at)}</>}
                </p>
              ) : (
                <p className="text-sm text-slate-500 mt-1">
                  Enable to get real-time processing — no 15-minute wait when new files land in OneDrive.
                </p>
              )}
              {active && (
                <p className="text-xs text-slate-400 mt-0.5">
                  Expires in {fmtDuration(status.seconds_remaining)} · auto-renews when &lt;24h remain
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!active ? (
              <Button
                data-testid="live-deltas-enable-btn"
                onClick={onCreate}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {busy ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Bolt className="h-4 w-4 mr-1" />}
                Enable Live Deltas
              </Button>
            ) : (
              <>
                <Button
                  data-testid="live-deltas-renew-btn"
                  onClick={onRenew}
                  disabled={busy}
                  variant="outline"
                >
                  {busy ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
                  Renew
                </Button>
                <Button
                  data-testid="live-deltas-disable-btn"
                  onClick={onDelete}
                  disabled={busy}
                  variant="outline"
                  className="text-red-600 hover:text-red-700"
                >
                  <PowerOff className="h-4 w-4 mr-1" />
                  Disable
                </Button>
              </>
            )}
          </div>
        </div>

        {active && events && events.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Recent notifications</p>
            <div className="space-y-1.5 max-h-40 overflow-y-auto">
              {events.slice(0, 8).map((ev, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <Bolt className="h-3 w-3 text-emerald-500" />
                  <span className="text-slate-700 dark:text-slate-300">
                    {ev.accepted} accepted{ev.rejected > 0 && <span className="text-red-600 ml-1">, {ev.rejected} rejected</span>}
                  </span>
                  <span className="text-slate-400 ml-auto">{fmtRelative(ev.received_at)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// AutoSync Widget
// ----------------------------------------------------------------------------
function AutoSyncCard({ status, onToggle, onRunNow, onForceFullScan, onResetDeltaCursor, runningNow, resettingDelta }) {
  if (!status) return <Skeleton className="h-32 w-full" />;
  const stats = status.stats || {};
  return (
    <Card data-testid="autosync-status-card" className="border-2 border-violet-200 dark:border-violet-900">
      <CardContent className="p-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-4">
            <div className={`relative h-14 w-14 rounded-2xl ${status.is_enabled ? 'bg-violet-600' : 'bg-slate-400'} flex items-center justify-center shadow-lg`}>
              <Cloud className="h-7 w-7 text-white" />
              {status.is_enabled && (
                <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 ring-2 ring-white animate-pulse" />
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">OneDrive Auto-Sync</h2>
              <p className="text-sm text-slate-500 mt-1">
                Runs every {status.interval_minutes} min · last run {fmtRelative(status.last_run_at)} · {stats.total_runs ?? 0} total runs · {stats.total_files_added ?? 0} files auto-added
              </p>
              {status.next_run_at && status.is_enabled && (
                <p className="text-xs text-slate-400 mt-0.5">Next run {fmtRelative(status.next_run_at)}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-500">Autonomous</span>
              <Switch
                data-testid="autosync-toggle"
                checked={status.is_enabled}
                onCheckedChange={onToggle}
              />
            </div>
            <Button
              data-testid="autosync-run-now"
              onClick={onRunNow}
              disabled={runningNow}
              className="bg-violet-600 hover:bg-violet-700"
            >
              {runningNow ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Sync now
            </Button>
            <Button
              data-testid="autosync-force-full"
              onClick={onForceFullScan}
              disabled={runningNow}
              variant="outline"
              title="Wipe delta cursor and walk every folder from scratch. Use when 'Sync now' keeps reporting 0 new files but you know files exist in OneDrive."
            >
              <RefreshCw className="h-4 w-4 mr-1" />
              Force full re-scan
            </Button>
            <Button
              data-testid="autosync-reset-delta"
              onClick={onResetDeltaCursor}
              disabled={resettingDelta}
              variant="ghost"
              size="sm"
              title="Clear the delta cursor only — the next regular sync will then do a full scan."
            >
              {resettingDelta ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Reset cursor'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ----------------------------------------------------------------------------
// Summary cards (counts by status)
// ----------------------------------------------------------------------------
function SummaryCards({ summary, onClick, activeStatus }) {
  if (!summary) return null;
  const order = ['unsynced', 'pending_processing', 'processing', 'completed', 'failed', 'file_missing', 'quarantined'];
  const entries = order
    .map(k => [k, summary.by_status?.[k] ?? 0])
    .filter(([_, v]) => v > 0 || ['unsynced', 'failed', 'file_missing'].includes(_));

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="summary-cards">
      {entries.map(([key, count]) => {
        const m = STATUS_META[key] || STATUS_META.unknown;
        const Icon = m.icon;
        const isActive = activeStatus === key;
        return (
          <button
            key={key}
            data-testid={`summary-card-${key}`}
            onClick={() => onClick(isActive ? null : key)}
            className={`p-4 rounded-xl border-2 transition-all text-left ${
              isActive
                ? 'border-violet-500 shadow-lg bg-violet-50 dark:bg-violet-950/30'
                : 'border-slate-200 dark:border-slate-800 hover:border-slate-300'
            }`}
          >
            <Icon className={`h-5 w-5 mb-2 ${isActive ? 'text-violet-600' : 'text-slate-400'}`} />
            <p className="text-xs text-slate-500 uppercase tracking-wider">{m.label}</p>
            <p className="text-2xl font-semibold mt-1">{count.toLocaleString()}</p>
          </button>
        );
      })}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Failure Summary Card (Feb 2026) — admins SEE WHY calls are failing,
// clustered by canonical error reason, with one-click retry-all.
// ----------------------------------------------------------------------------
function FailureSummaryCard({ summary, onRetryAll, retryingAll }) {
  if (!summary || summary.total_failed === 0) return null;
  return (
    <Card data-testid="failure-summary-card" className="border-2 border-red-200 dark:border-red-900 bg-red-50/40 dark:bg-red-950/10">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2 min-w-0">
          <AlertCircle className="h-5 w-5 text-red-600 shrink-0" />
          <div>
            <CardTitle className="text-base">
              {summary.total_failed.toLocaleString()} failed call{summary.total_failed === 1 ? '' : 's'}
            </CardTitle>
            <CardDescription className="text-xs mt-0.5">
              Top failure reasons — fix the root cause, then click <strong>Retry all failed</strong>
            </CardDescription>
          </div>
        </div>
        <Button
          data-testid="retry-all-failed-btn"
          variant="default"
          size="sm"
          onClick={onRetryAll}
          disabled={retryingAll}
          className="bg-red-600 hover:bg-red-700 text-white shrink-0"
        >
          {retryingAll ? <RefreshCw className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCw className="h-3.5 w-3.5 mr-1" />}
          {retryingAll ? 'Retrying…' : 'Retry all failed'}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-1.5">
          {(summary.by_reason || []).slice(0, 8).map((b, i) => (
            <div
              key={i}
              data-testid={`failure-reason-${i}`}
              className="flex items-start gap-3 p-2 rounded-lg bg-white dark:bg-slate-900/40 border border-red-100 dark:border-red-900/40"
            >
              <div className="flex flex-col items-center justify-center w-14 shrink-0">
                <span className="text-lg font-bold text-red-700 dark:text-red-300">{b.count.toLocaleString()}</span>
                <span className="text-[9px] text-red-600 uppercase tracking-wider">calls</span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">{b.reason}</p>
                {b.last_failed_at && (
                  <p className="text-[10px] text-slate-500 mt-0.5">Last failure {fmtRelative(b.last_failed_at)}</p>
                )}
                {b.sample_call_ids && b.sample_call_ids.length > 0 && (
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    e.g.&nbsp;
                    {b.sample_call_ids.slice(0, 3).map((cid, j) => (
                      <a
                        key={cid}
                        href={`/dashboard/call/${cid}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline hover:text-slate-600"
                      >
                        {cid.slice(0, 8)}{j < Math.min(b.sample_call_ids.length, 3) - 1 ? ', ' : ''}
                      </a>
                    ))}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}


// ----------------------------------------------------------------------------
// Folder Tree (Feb 2026) — LIVE OneDrive walker. Reads the connected
// OneDrive folder structure starting from the user's selected root, lazy
// loads children on demand, overlays inventory stats per folder.
// ----------------------------------------------------------------------------
function FolderTreeCard({
  rootFolders,
  rootPath,
  expandedIds,
  childrenById,
  loadingIds,
  onToggle,
  onSync,
  syncingPath,
  errorMsg,
  onRefresh,
  isLoading,
}) {
  const renderRow = (f, depth = 0) => {
    const isExpanded = expandedIds.has(f.id);
    const isLoading = loadingIds.has(f.id);
    const kids = childrenById[f.id] || [];
    const isSyncing = syncingPath === f.path;
    const inv = f.inventory;
    const indent = Math.min(depth, 10) * 16;
    return (
      <div key={f.id} data-testid={`folder-tree-row-${f.id}`}>
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-slate-50 dark:hover:bg-slate-900/40 group"
          style={{ paddingLeft: `${indent + 8}px` }}
        >
          {f.has_subfolders ? (
            <button
              data-testid={`folder-tree-toggle-${f.id}`}
              onClick={() => onToggle(f)}
              className="h-5 w-5 flex items-center justify-center text-slate-500 hover:text-slate-700 shrink-0"
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isLoading
                ? <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                : isExpanded
                  ? <ChevronDown className="h-3.5 w-3.5" />
                  : <ChevronRight className="h-3.5 w-3.5" />
              }
            </button>
          ) : (
            <span className="h-5 w-5 shrink-0" />
          )}
          {isExpanded ? <FolderOpen className="h-4 w-4 text-amber-500 shrink-0" /> : <Folder className="h-4 w-4 text-amber-500 shrink-0" />}
          <span className="font-medium text-sm text-slate-800 dark:text-slate-200 truncate min-w-0" title={f.path}>
            {f.name}
          </span>
          {f.child_count > 0 && (
            <span className="text-[10px] text-slate-400">
              {f.child_count} item{f.child_count === 1 ? '' : 's'}
            </span>
          )}
          {inv && inv.total > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50">
              {inv.total.toLocaleString()} synced
            </Badge>
          )}
          {inv?.by_status?.completed > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-emerald-50 text-emerald-700 border-emerald-200" title="Completed">
              ✓ {inv.by_status.completed}
            </Badge>
          )}
          {((inv?.by_status?.pending || 0) + (inv?.by_status?.processing || 0)) > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200" title="Pending or processing">
              ⏳ {(inv.by_status.pending || 0) + (inv.by_status.processing || 0)}
            </Badge>
          )}
          {inv?.by_status?.failed > 0 && (
            <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-red-50 text-red-700 border-red-200" title="Failed">
              ✕ {inv.by_status.failed}
            </Badge>
          )}
          <div className="flex-1" />
          {inv?.last_seen_at && (
            <span className="text-[10px] text-slate-400 hidden md:inline whitespace-nowrap">
              {fmtRelative(inv.last_seen_at)}
            </span>
          )}
          <Button
            data-testid={`folder-tree-sync-${f.id}`}
            variant="ghost"
            size="sm"
            disabled={isSyncing}
            onClick={() => onSync(f)}
            className="opacity-0 group-hover:opacity-100 transition h-7 px-2"
            title={`Sync ${f.path} and all sub-folders now`}
          >
            {isSyncing ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Zap className="h-3 w-3" />}
            <span className="ml-1 text-xs">Sync</span>
          </Button>
        </div>
        {isExpanded && (
          <div>
            {kids.length > 0
              ? kids.map((k) => renderRow(k, depth + 1))
              : !isLoading && (
                  <div
                    className="text-[11px] text-slate-400 italic py-1"
                    style={{ paddingLeft: `${indent + 32}px` }}
                  >
                    (no sub-folders)
                  </div>
                )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card data-testid="folder-tree-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="min-w-0">
          <CardTitle className="text-base flex items-center gap-2">
            <Folder className="h-4 w-4 text-amber-500" />
            OneDrive folder tree
          </CardTitle>
          <CardDescription className="text-xs mt-0.5 truncate" title={rootPath}>
            Live view of <strong>{rootPath || '(no folder selected)'}</strong> · click to expand · hover for &quot;Sync&quot;
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" onClick={onRefresh} title="Refresh tree" data-testid="folder-tree-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </CardHeader>
      <CardContent>
        {errorMsg && (
          <div data-testid="folder-tree-error" className="p-3 mb-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">
            <p className="font-medium">Cannot load live OneDrive tree</p>
            <p className="text-xs mt-1">{errorMsg}</p>
            <p className="text-xs mt-1">
              Make sure OneDrive is connected on the <a href="/dashboard/onedrive" className="underline">OneDrive page</a> and a folder is selected.
            </p>
          </div>
        )}
        {!errorMsg && isLoading && rootFolders.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500">
            <RefreshCw className="h-6 w-6 mx-auto mb-2 animate-spin text-slate-400" />
            Loading OneDrive folder structure…
          </div>
        )}
        {!errorMsg && !isLoading && rootFolders.length === 0 && (
          <div className="text-center py-8 text-sm text-slate-500" data-testid="folder-tree-empty">
            <Folder className="h-8 w-8 mx-auto mb-2 text-slate-300" />
            <p>The selected folder has no sub-folders.</p>
            <p className="text-xs mt-1">
              Click <strong>Sync now</strong> above to scan it for audio files — any nested sub-folders will appear here too.
            </p>
          </div>
        )}
        {rootFolders.length > 0 && (
          <ScrollArea className="h-[520px] pr-2">
            {rootFolders.map((f) => renderRow(f, 0))}
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}


// ----------------------------------------------------------------------------
// Main page
// ----------------------------------------------------------------------------
export default function AdminOneDriveInventory() {
  const [status, setStatus]       = useState(null);
  const [summary, setSummary]     = useState(null);
  const [items, setItems]         = useState([]);
  const [pagination, setPagination] = useState({ page: 1, page_size: 50, total: 0, pages: 0 });
  const [runs, setRuns]           = useState([]);
  const [filters, setFilters]     = useState({ status: null, search: '' });
  const [loading, setLoading]     = useState(true);
  const [runningNow, setRunningNow] = useState(false);
  const [selected, setSelected]   = useState(new Set());
  const [bulkBusy, setBulkBusy]   = useState(false);
  const [subStatus, setSubStatus] = useState(null);
  const [subEvents, setSubEvents] = useState([]);
  const [subBusy, setSubBusy]     = useState(false);

  // Folder tree (Feb 2026) — LIVE OneDrive walker (lazy loaded per-expand)
  const [treeRoot, setTreeRoot] = useState({ folders: [], path: '', id: null });
  const [treeChildren, setTreeChildren] = useState({}); // folderId -> [child folders]
  const [treeExpanded, setTreeExpanded] = useState(new Set());
  const [treeLoading, setTreeLoading] = useState(new Set()); // folder ids currently loading
  const [treeError, setTreeError] = useState(null);
  const [treeInitialLoading, setTreeInitialLoading] = useState(false);
  const [folderSyncing, setFolderSyncing] = useState(null); // path being synced
  const [resettingDelta, setResettingDelta] = useState(false);
  const [failureSummary, setFailureSummary] = useState(null);
  const [retryingAll, setRetryingAll] = useState(false);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [st, sm, ru, ss, se] = await Promise.all([
        api('/api/onedrive/auto-sync/status'),
        api('/api/onedrive/inventory/summary'),
        api('/api/onedrive/auto-sync/runs?limit=20'),
        api('/api/onedrive/subscription/status'),
        api('/api/onedrive/subscription/events?limit=10'),
      ]);
      setStatus(st); setSummary(sm); setRuns(ru.runs || []);
      setSubStatus(ss); setSubEvents(se.events || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        page_size: String(pagination.page_size),
      });
      if (filters.status) params.set('status', filters.status);
      if (filters.search) params.set('search', filters.search);
      const r = await api(`/api/onedrive/inventory?${params.toString()}`);
      setItems(r.items || []);
      setPagination(p => ({ ...p, total: r.total, pages: r.pages }));
    } catch (e) {
      console.error(e);
    }
  }, [pagination.page, pagination.page_size, filters.status, filters.search]);

  // ---- Folder tree (Feb 2026) — LIVE OneDrive walker ----
  const fetchTreeNode = useCallback(async (folderId = null) => {
    const params = folderId ? `?folder_id=${encodeURIComponent(folderId)}` : '';
    const r = await api(`/api/onedrive/inventory/live-folder-tree${params}`);
    return r;
  }, []);

  const fetchTreeRoot = useCallback(async () => {
    setTreeInitialLoading(true);
    setTreeError(null);
    try {
      const r = await fetchTreeNode(null);
      setTreeRoot({ folders: r.folders || [], path: r.folder_path || '', id: r.folder_id });
      setTreeChildren({}); // reset children cache when root reloads
      setTreeExpanded(new Set());
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || 'Failed to load OneDrive tree';
      setTreeError(msg);
      setTreeRoot({ folders: [], path: '', id: null });
    } finally {
      setTreeInitialLoading(false);
    }
  }, [fetchTreeNode]);

  // Failure summary (Feb 2026) — surface WHY calls are failing
  const fetchFailureSummary = useCallback(async () => {
    try {
      const r = await api('/api/calls/failure-summary');
      setFailureSummary(r);
    } catch (e) {
      console.error('failure-summary fetch failed', e);
    }
  }, []);

  const onRetryAllFailed = async () => {
    if (!window.confirm(
      'Retry every failed call? This will (a) refresh the OneDrive token, (b) check each file still exists in OneDrive, (c) re-queue the rest for processing. Files that have been deleted from OneDrive will be marked file_missing and skipped.'
    )) return;
    setRetryingAll(true);
    try {
      const r = await api('/api/calls/retry-failed?verify_files=true', { method: 'POST' });
      toast.success(
        `Retry queued — ${r.count || 0} call(s) re-queued · token refreshed: ${r.token_refreshed ? 'yes' : 'no'}` +
        (r.file_missing ? ` · ${r.file_missing} file(s) missing from OneDrive` : '')
      );
      await Promise.all([fetchStatus(), fetchFailureSummary()]);
    } catch (e) {
      toast.error(`Retry failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setRetryingAll(false);
    }
  };

  useEffect(() => {
    Promise.all([fetchStatus(), fetchItems(), fetchTreeRoot(), fetchFailureSummary()]).finally(() => setLoading(false));
    pollRef.current = setInterval(() => {
      fetchStatus();
      fetchFailureSummary();
    }, 20000);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus, fetchItems, fetchTreeRoot, fetchFailureSummary]);

  useEffect(() => { fetchItems(); }, [pagination.page, filters.status, filters.search, fetchItems]);

  const onToggle = async (checked) => {
    try {
      await api(`/api/onedrive/auto-sync/toggle?enabled=${checked}`, { method: 'POST' });
      toast.success(checked ? 'Autonomous sync enabled' : 'Autonomous sync paused');
      fetchStatus();
    } catch (e) { toast.error('Toggle failed'); }
  };

  const onRunNow = async () => {
    setRunningNow(true);
    try {
      const r = await api('/api/onedrive/auto-sync/run-now', { method: 'POST' });
      if (r.status === 'ok') {
        const folderInfo = r.folders_crawled
          ? ` · walked ${r.folders_crawled} folder${r.folders_crawled === 1 ? '' : 's'}${(r.folders_failed || []).length ? ` (${r.folders_failed.length} failed)` : ''}`
          : '';
        toast.success(`Sync complete — found ${r.files_in_onedrive} file(s), added ${r.new_files_added}, marked ${r.marked_missing} missing${folderInfo}`);
        if ((r.folders_failed || []).length) {
          const sample = r.folders_failed.slice(0, 3).map(f => f.path).join(', ');
          toast.warning(`${r.folders_failed.length} folder(s) failed to scan: ${sample}${r.folders_failed.length > 3 ? '…' : ''}`);
        }
      } else if (r.status === 'skipped') {
        toast.info(r.error || 'Sync skipped');
      } else {
        toast.error(r.error || 'Sync failed');
      }
      await Promise.all([fetchStatus(), fetchItems()]);
    } catch (e) { toast.error(`Sync failed: ${e.message}`); }
    finally { setRunningNow(false); }
  };

  const onSelect = (fid, checked) => {
    setSelected(prev => {
      const s = new Set(prev);
      if (checked) s.add(fid); else s.delete(fid);
      return s;
    });
  };

  // ---- Folder tree (Feb 2026) ---- handlers (fetchers declared above) ----

  const onToggleFolder = useCallback(async (folder) => {
    const id = folder.id;
    if (treeExpanded.has(id)) {
      setTreeExpanded(prev => { const s = new Set(prev); s.delete(id); return s; });
      return;
    }
    setTreeExpanded(prev => new Set(prev).add(id));
    if (!treeChildren[id] && folder.has_subfolders) {
      setTreeLoading(prev => new Set(prev).add(id));
      try {
        const r = await fetchTreeNode(id);
        setTreeChildren(prev => ({ ...prev, [id]: r.folders || [] }));
      } catch (e) {
        console.error('expand failed:', e);
        toast.error(`Could not list children of ${folder.name}: ${e?.response?.data?.detail || e.message}`);
      } finally {
        setTreeLoading(prev => { const s = new Set(prev); s.delete(id); return s; });
      }
    }
  }, [treeExpanded, treeChildren, fetchTreeNode]);

  const onSyncFolder = async (folder) => {
    const label = folder.name || folder.path;
    setFolderSyncing(folder.path);
    try {
      const r = await api('/api/onedrive/inventory/sync-folder', {
        method: 'POST',
        body: JSON.stringify({ folder_path: folder.id }),  // id is precise; backend accepts both
      });
      const extra = (r.folders_failed || []).length
        ? ` · ${r.folders_failed.length} folder fail(s)`
        : '';
      toast.success(
        `Synced ${label} — ${r.files_in_onedrive} files, +${r.new_files_added} new, walked ${r.folders_crawled} folder(s)${extra}`,
      );
      await Promise.all([fetchStatus(), fetchTreeRoot()]);
    } catch (e) {
      toast.error(`Folder sync failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setFolderSyncing(null);
    }
  };

  const onForceFullScan = async () => {
    if (!window.confirm(
      'Force a full re-scan? This wipes the delta cursor and walks every folder from scratch. ' +
      'Use this when you suspect inventory is missing files.'
    )) return;
    setRunningNow(true);
    try {
      const r = await api('/api/onedrive/auto-sync/run-now?force_full=true', { method: 'POST' });
      if (r.status === 'ok') {
        toast.success(
          `Full scan complete — ${r.files_in_onedrive} files found, +${r.new_files_added} new, ` +
          `${r.folders_crawled || 0} folder(s) walked`
        );
        if ((r.folders_failed || []).length) {
          toast.warning(`${r.folders_failed.length} folder(s) failed`);
        }
      } else if (r.status === 'skipped') {
        toast.info(r.error || 'Sync skipped');
      } else {
        toast.error(r.error || 'Sync failed');
      }
      await Promise.all([fetchStatus(), fetchItems(), fetchTreeRoot()]);
    } catch (e) {
      toast.error(`Force-full failed: ${e.message}`);
    } finally {
      setRunningNow(false);
    }
  };

  const onResetDeltaCursor = async () => {
    if (!window.confirm(
      'Reset the OneDrive delta cursor? ' +
      "The next sync will walk every folder from scratch. " +
      "Use this when you keep seeing '0 new files' but you know files exist in OneDrive."
    )) return;
    setResettingDelta(true);
    try {
      const r = await api('/api/onedrive/auto-sync/delta-cursor', { method: 'DELETE' });
      toast.success(r.message || 'Delta cursor reset');
      await fetchStatus();
    } catch (e) {
      toast.error(`Reset failed: ${e.message}`);
    } finally {
      setResettingDelta(false);
    }
  };

  const onBulkRequeue = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const r = await api('/api/onedrive/inventory/requeue', {
        method: 'POST',
        body: JSON.stringify(Array.from(selected)),
      });
      toast.success(`Re-queued ${r.requeued} existing call(s), created ${r.created} new`);
      setSelected(new Set());
      await Promise.all([fetchStatus(), fetchItems()]);
    } catch (e) { toast.error(`Re-queue failed: ${e.message}`); }
    finally { setBulkBusy(false); }
  };

  // ---- Live Deltas handlers ----
  const onEnableLiveDeltas = async () => {
    setSubBusy(true);
    try {
      const r = await api('/api/onedrive/subscription/create', { method: 'POST' });
      toast.success('Live Deltas enabled — Microsoft will now push changes instantly');
      await fetchStatus();
    } catch (e) {
      toast.error(`Failed to enable: ${e.message}`);
    } finally { setSubBusy(false); }
  };

  const onRenewLiveDeltas = async () => {
    setSubBusy(true);
    try {
      const r = await api('/api/onedrive/subscription/renew', { method: 'POST' });
      toast.success('Live Deltas renewed — fresh 7-day expiry');
      await fetchStatus();
    } catch (e) {
      toast.error(`Renew failed: ${e.message}`);
    } finally { setSubBusy(false); }
  };

  const onDisableLiveDeltas = async () => {
    if (!window.confirm('Disable Live Deltas? You will fall back to 15-minute polling.')) return;
    setSubBusy(true);
    try {
      await api('/api/onedrive/subscription', { method: 'DELETE' });
      toast.success('Live Deltas disabled — auto-sync still runs every 15 min');
      await fetchStatus();
    } catch (e) {
      toast.error(`Disable failed: ${e.message}`);
    } finally { setSubBusy(false); }
  };

  if (loading) {
    return <div className="p-6 space-y-4"><Skeleton className="h-32" /><Skeleton className="h-24" /><Skeleton className="h-96" /></div>;
  }

  return (
    <div data-testid="admin-onedrive-inventory" className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
          <Database className="h-8 w-8 text-violet-600" />
          OneDrive Inventory
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Every audio file ever seen in your watched OneDrive folder, with its current status.
          See exactly which files are missing, failed, or waiting to be processed.
        </p>
      </div>

      {/* AutoSync card */}
      <AutoSyncCard
        status={status}
        onToggle={onToggle}
        onRunNow={onRunNow}
        onForceFullScan={onForceFullScan}
        onResetDeltaCursor={onResetDeltaCursor}
        runningNow={runningNow}
        resettingDelta={resettingDelta}
      />

      {/* Failure summary — shows up automatically when there are failed calls */}
      <FailureSummaryCard
        summary={failureSummary}
        onRetryAll={onRetryAllFailed}
        retryingAll={retryingAll}
      />

      {/* Live Deltas card */}
      <LiveDeltasCard
        status={subStatus}
        events={subEvents}
        onCreate={onEnableLiveDeltas}
        onRenew={onRenewLiveDeltas}
        onDelete={onDisableLiveDeltas}
        busy={subBusy}
      />

      {/* Summary cards */}
      <SummaryCards
        summary={summary}
        onClick={(s) => { setPagination(p => ({ ...p, page: 1 })); setFilters(f => ({ ...f, status: s })); }}
        activeStatus={filters.status}
      />

      <Tabs defaultValue="files" className="w-full">
        <TabsList>
          <TabsTrigger data-testid="tab-files" value="files">
            Files
            <Badge className="ml-2" variant="outline">{pagination.total.toLocaleString()}</Badge>
          </TabsTrigger>
          <TabsTrigger data-testid="tab-folder-tree" value="folder-tree">
            Folder tree
            {treeRoot.folders.length > 0 && <Badge variant="outline" className="ml-2">{treeRoot.folders.length}</Badge>}
          </TabsTrigger>
          <TabsTrigger data-testid="tab-runs" value="runs">Sync history</TabsTrigger>
        </TabsList>

        <TabsContent value="files" className="space-y-3 mt-4">
          {/* Search + bulk actions */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[280px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    data-testid="inventory-search"
                    placeholder="Search by filename or path..."
                    value={filters.search}
                    onChange={(e) => { setPagination(p => ({ ...p, page: 1 })); setFilters(f => ({ ...f, search: e.target.value })); }}
                    className="pl-9"
                  />
                </div>
                <Select
                  value={filters.status || 'all'}
                  onValueChange={(v) => { setPagination(p => ({ ...p, page: 1 })); setFilters(f => ({ ...f, status: v === 'all' ? null : v })); }}
                >
                  <SelectTrigger data-testid="inventory-status-filter" className="w-[200px]">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.entries(STATUS_META).filter(([k]) => k !== 'unknown').map(([k, m]) => (
                      <SelectItem key={k} value={k}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selected.size > 0 && (
                  <Button
                    data-testid="bulk-requeue-btn"
                    onClick={onBulkRequeue}
                    disabled={bulkBusy}
                    className="bg-violet-600 hover:bg-violet-700"
                  >
                    {bulkBusy ? <RefreshCw className="h-4 w-4 mr-1 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-1" />}
                    Re-queue {selected.size} file(s)
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <input
                        type="checkbox"
                        data-testid="select-all-checkbox"
                        checked={items.length > 0 && items.every(i => selected.has(i.file_id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelected(new Set([...selected, ...items.map(i => i.file_id)]));
                          } else {
                            const s = new Set(selected);
                            items.forEach(i => s.delete(i.file_id));
                            setSelected(s);
                          }
                        }}
                      />
                    </TableHead>
                    <TableHead>File</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Size</TableHead>
                    <TableHead>Last modified</TableHead>
                    <TableHead>Last seen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-10 text-sm text-slate-500">
                        No files in inventory yet. Click "Sync now" above to scan OneDrive.
                      </TableCell>
                    </TableRow>
                  ) : items.map((row) => (
                    <TableRow key={row.file_id} data-testid={`inventory-row-${row.file_id}`}>
                      <TableCell>
                        <input
                          type="checkbox"
                          checked={selected.has(row.file_id)}
                          onChange={(e) => onSelect(row.file_id, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileAudio className="h-4 w-4 text-slate-400 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{row.name}</p>
                            <p className="text-xs text-slate-500 truncate">{row.path}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={row.status} /></TableCell>
                      <TableCell className="text-sm text-slate-600">{fmtBytes(row.size_bytes)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmtRelative(row.last_modified)}</TableCell>
                      <TableCell className="text-xs text-slate-500">{fmtRelative(row.last_seen_at)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {/* Pagination */}
              {pagination.pages > 1 && (
                <div className="flex items-center justify-between p-4 border-t" data-testid="pagination-bar">
                  <p className="text-sm text-slate-500">
                    Page {pagination.page} of {pagination.pages} · {pagination.total.toLocaleString()} files
                  </p>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={pagination.page <= 1}
                      onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                    >Previous</Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={pagination.page >= pagination.pages}
                      onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                    >Next</Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="folder-tree" className="mt-4">
          <FolderTreeCard
            rootFolders={treeRoot.folders}
            rootPath={treeRoot.path}
            expandedIds={treeExpanded}
            childrenById={treeChildren}
            loadingIds={treeLoading}
            onToggle={onToggleFolder}
            onSync={onSyncFolder}
            syncingPath={folderSyncing}
            errorMsg={treeError}
            onRefresh={fetchTreeRoot}
            isLoading={treeInitialLoading}
          />
        </TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Recent sync runs ({runs.length})
              </CardTitle>
              <CardDescription>Audit log of every auto-sync run — manual or scheduled.</CardDescription>
            </CardHeader>
            <CardContent>
              {runs.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">No sync runs yet.</p>
              ) : (
                <ScrollArea className="h-[500px] pr-3">
                  {runs.map((r) => (
                    <div key={r.run_id} className="py-3 border-b last:border-0">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge
                            variant="outline"
                            className={r.status === 'ok' ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                              : r.status === 'skipped' ? 'bg-slate-50 text-slate-600 border-slate-200'
                              : 'bg-red-50 text-red-700 border-red-200'}>
                            {r.status}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{r.trigger}</Badge>
                          {r.enumeration_mode && (
                            <Badge
                              variant="outline"
                              className={`text-[10px] px-1.5 py-0 ${r.enumeration_mode === 'delta'
                                ? 'bg-sky-50 text-sky-700 border-sky-200'
                                : 'bg-violet-50 text-violet-700 border-violet-200'}`}
                              title={r.enumeration_mode === 'delta' ? 'Cheap incremental sync via Microsoft Graph delta tokens' : 'Full tree walk — every folder visited'}
                            >
                              {r.enumeration_mode}
                            </Badge>
                          )}
                          <span className="text-sm font-medium">
                            {r.files_in_onedrive ?? 0} files · +{r.new_files_added ?? 0} new · {r.marked_missing ?? 0} missing
                          </span>
                          {typeof r.folders_crawled === 'number' && r.folders_crawled > 0 && (
                            <span
                              className="text-xs text-slate-500"
                              data-testid={`sync-run-folders-${r.run_id}`}
                              title="Number of OneDrive folders walked during this sync"
                            >
                              · {r.folders_crawled} folder{r.folders_crawled === 1 ? '' : 's'}
                            </span>
                          )}
                          {(r.folders_failed || []).length > 0 && (
                            <Badge
                              variant="outline"
                              className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0"
                              data-testid={`sync-run-failed-${r.run_id}`}
                            >
                              {r.folders_failed.length} folder fail{r.folders_failed.length === 1 ? '' : 's'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-500">
                          {fmtRelative(r.started_at)} · {r.duration_ms}ms
                          {r.processor_triggered && <span className="ml-2 text-violet-600">· processor started</span>}
                        </p>
                      </div>
                      {r.error && (
                        <p className="text-xs text-red-600 mt-1 truncate">{r.error}</p>
                      )}
                      {(r.folders_failed || []).length > 0 && (
                        <details className="mt-1">
                          <summary className="text-[11px] text-amber-700 cursor-pointer select-none">
                            Show {r.folders_failed.length} failed folder path{r.folders_failed.length === 1 ? '' : 's'}
                          </summary>
                          <ul className="mt-1 pl-3 space-y-0.5 text-[11px] text-slate-600 font-mono">
                            {r.folders_failed.slice(0, 25).map((f, i) => (
                              <li key={i} className="truncate" title={`${f.path} — ${f.error}`}>
                                <span className="text-amber-700">{f.path || '/'}</span> — {f.error}
                              </li>
                            ))}
                            {r.folders_failed.length > 25 && (
                              <li className="text-slate-400">…+{r.folders_failed.length - 25} more</li>
                            )}
                          </ul>
                        </details>
                      )}
                    </div>
                  ))}
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
